/**
 * App component file.
 * @module src/components/App
 */
/* eslint-disable no-console */
import React, { FC, useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { ApolloProvider } from '@apollo/react-hooks';
import queryString from 'query-string';
import Routes from '../Routes';
import client from '../../graphql/client';
import { valid } from '../../util/env';
import store, { persistor } from '../../store';
import GET_SUBMISSION_STATUS from '../../graphql/getSubmissionStatus';
import GET_SUBMISSION_STATUS_BY_CASE from '../../graphql/getSubmissionStatusByCase';
import { submissionPolling, SubmissionStatusByCaseRecord } from '../../store/submissionPolling';
import { SubmissionStatusRecord } from '../../util/submissionState';
import { INVENTORY_REQUEST_DETAIL_UI } from '../../util/constants';
import useResumeSubmissionChecksOnReturn from '../../util/customHooks/useResumeSubmissionChecksOnReturn';
import './index.scss';
import CustomFooter from './CustomFooter';
import { AppDataContextProvider } from '../app-data/app-data-provider';

const { grant_token: grantToken } = queryString.parse(window.location.search);
if (grantToken !== undefined && grantToken !== null && !Array.isArray(grantToken)) {
  window.history.replaceState({}, document.title, '/');
}

const ReduxApolloApp: FC = () => {
  useEffect(() => {
    submissionPolling.configure({
      dispatch: store.dispatch,
      fetchSubmissionStatus: async submissionId => {
        const result = await client.query({
          query: GET_SUBMISSION_STATUS,
          variables: { submissionId },
          fetchPolicy: 'no-cache',
        });
        return (result.data as { getSubmissionStatus?: SubmissionStatusRecord })
          ?.getSubmissionStatus;
      },
      fetchSubmissionStatusByCase: async caseExternalId => {
        const result = await client.query({
          query: GET_SUBMISSION_STATUS_BY_CASE,
          variables: { caseExternalId },
          fetchPolicy: 'no-cache',
        });
        return (result.data as { getSubmissionStatusByCase?: SubmissionStatusByCaseRecord })
          ?.getSubmissionStatusByCase;
      },
      pollDelayMs: INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_POLL_INTERVAL_MS,
    });
  }, []);

  useResumeSubmissionChecksOnReturn();

  return (
    <IconSettings iconPath="/icons">
      <div />
    </IconSettings>
  );
};

const ReduxApp: FC = () => {
  return <ReduxApolloApp />;
};

const App: FC = () => {
  return valid ? (
    <ApolloProvider client={client}>
      <AppDataContextProvider>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <Routes />
            <ReduxApp />
            <CustomFooter />
          </PersistGate>
        </Provider>
      </AppDataContextProvider>
    </ApolloProvider>
  ) : (
    <div>
      <p>Invalid build environment</p>
    </div>
  );
};

/** App component */
export default App;
